// CalendarView.jsx
//
// Holiday management + calendar factor editing. The holiday list is
// fetched from GET /forecast/holidays and grouped by type. Each row
// has Edit + Delete actions; "+ New Holiday" opens the form modal;
// "✦ Edit factors" opens the calendar factor editor (day-of-month /
// day-of-week / holiday multipliers behind the Compare projection).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, CircularProgress, Typography, Snackbar,
  Select, MenuItem, FormControlLabel, Switch, IconButton,
} from "@mui/material";

import {
  getForecastHolidays, deleteHoliday,
  getCalendarDays,
} from "../../services/forecast/forecastservices";
import HolidayFormModal from "./modals/HolidayFormModal";
import CalendarFactorModal from "./modals/CalendarFactorModal";
import {
  BLUE, GREEN, AMBER, PURPLE, RED,
  ActionButton, EmptyState, SectionTitle,
} from "./forecastShared";


// ── Holiday-type colors ────────────────────────────────────────────
function typeColor(type) {
  switch (String(type || "").toLowerCase()) {
    case "federal":    return BLUE;
    case "state":      return AMBER;
    case "behavioral": return PURPLE;
    default:           return "#888";
  }
}

function TypePill({ type }) {
  const c = typeColor(type);
  return (
    <Box component="span" sx={{
      display: "inline-block", px: "7px", py: "1px",
      borderRadius: "20px", fontSize: 11, fontWeight: 500,
      bgcolor: `${c}18`, color: c, border: `0.5px solid ${c}44`,
      textTransform: "capitalize",
    }}>
      {type || "—"}
    </Box>
  );
}


// ── Rule-config friendly summary ───────────────────────────────────
function summarizeRule(rule_type, rule_config) {
  if (!rule_config) return rule_type || "—";
  try {
    const cfg = typeof rule_config === "string" ? JSON.parse(rule_config) : rule_config;
    const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

    if (rule_type === "fixed_date" && cfg.month && cfg.day) {
      return `${monthNames[cfg.month - 1]} ${cfg.day}`;
    }
    if (rule_type === "floating_dow" && cfg.month && cfg.week && cfg.dow != null) {
      const dowNames = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
      const ord = ["1st","2nd","3rd","4th","5th"][cfg.week - 1] || `${cfg.week}th`;
      const lastSpec = cfg.week === -1 ? "Last" : ord;
      return `${lastSpec} ${dowNames[cfg.dow]} of ${monthNames[cfg.month - 1]}`;
    }
    if (rule_type === "easter_offset" && cfg.offset_days != null) {
      const sign = cfg.offset_days >= 0 ? "+" : "";
      return `Easter ${sign}${cfg.offset_days}d`;
    }
    return rule_type;
  } catch {
    return rule_type;
  }
}


// ── Layout ─────────────────────────────────────────────────────────
const COLS = [
  { key: "name",          label: "Name",     align: "left",  width: "2fr"  },
  { key: "holiday_type",  label: "Type",     align: "left",  width: "1fr",   type: "type"    },
  { key: "rule_summary",  label: "Rule",     align: "left",  width: "1.5fr", type: "rule"    },
  { key: "is_active",     label: "Status",   align: "left",  width: "0.8fr", type: "status"  },
  { key: "actions",       label: "",         align: "right", width: "1fr",   type: "actions" },
];
const GRID = COLS.map(c => c.width).join(" ");



// ─────────────────────────────────────────────────────────────────────
//   CalendarDaysTable - paginated day-by-day view of analysis.calendar
// ─────────────────────────────────────────────────────────────────────
//
// Reads from GET /forecast/calendar with page / pageSize / futureOnly
// query params. Default state: futureOnly=true, pageSize=50, page=1.
// Renders a wide compact table with badges for day type and small
// numeric chips for the four payday-proximity columns.

const PAGE_SIZE_OPTIONS = [50, 100, 200];

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  // Sat, May 29 2026
  return d.toLocaleDateString("en-US", {
    weekday: "short", month: "short", day: "numeric", year: "numeric",
    timeZone: "UTC",
  });
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function holidayTypeColor(t) {
  switch (String(t || "").toLowerCase()) {
    case "federal":    return BLUE;
    case "state_ca":   return AMBER;
    case "behavioral": return PURPLE;
    default:           return "#888";
  }
}

// Small colored numeric chip for proximity values.
// Conventions for proximity_to_*:  0 = on that target, n = days until next.
// holiday_proximity uses 99 as the "none nearby" sentinel.
function ProxChip({ value, sentinel = null, color = "#666" }) {
  const num = Number(value);
  if (isNaN(num) || value === null || value === undefined) {
    return <Box component="span" sx={{ color: "text.disabled", fontSize: 11 }}>—</Box>;
  }
  if (sentinel !== null && num === sentinel) {
    return <Box component="span" sx={{ color: "text.disabled", fontSize: 11 }}>—</Box>;
  }
  const isToday = num === 0;
  return (
    <Box component="span" sx={{
      display: "inline-block",
      minWidth: 22, textAlign: "center",
      px: "5px", py: "0px",
      borderRadius: "10px", fontSize: 10, fontWeight: isToday ? 700 : 500,
      bgcolor: isToday ? `${color}` : `${color}15`,
      color:   isToday ? "#fff"     : color,
      border:  isToday ? "none"     : `0.5px solid ${color}33`,
    }}>
      {num}
    </Box>
  );
}

function DayTypeBadge({ row }) {
  if (row.is_holiday) {
    const c = holidayTypeColor(row.holiday_type);
    return (
      <Box component="span" sx={{
        display: "inline-block", px: "7px", py: "1px",
        borderRadius: "20px", fontSize: 10, fontWeight: 500,
        bgcolor: `${c}22`, color: c, border: `0.5px solid ${c}55`,
      }}>
        Holiday
      </Box>
    );
  }
  if (row.is_weekend) {
    return (
      <Box component="span" sx={{
        display: "inline-block", px: "7px", py: "1px",
        borderRadius: "20px", fontSize: 10, fontWeight: 500,
        bgcolor: "#88888818", color: "#888",
        border: "0.5px solid #88888844",
      }}>
        Weekend
      </Box>
    );
  }
  return (
    <Box component="span" sx={{
      display: "inline-block", px: "7px", py: "1px",
      borderRadius: "20px", fontSize: 10, fontWeight: 500,
      bgcolor: `${GREEN}15`, color: GREEN,
      border: `0.5px solid ${GREEN}33`,
    }}>
      Business
    </Box>
  );
}


function CalendarDaysTable() {
  const [rows,        setRows]        = useState([]);
  const [page,        setPage]        = useState(1);
  const [pageSize,    setPageSize]    = useState(50);
  const [futureOnly,  setFutureOnly]  = useState(true);
  const [total,       setTotal]       = useState(0);
  const [totalPages,  setTotalPages]  = useState(1);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState("");

  const today = useMemo(() => todayISO(), []);

  // Fetch on any control change. The endpoint accepts query-string params;
  // the service helper forwards them through axios.
  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const data = await getCalendarDays({
        page, pageSize,
        futureOnly: futureOnly ? "true" : "false",
      });
      // Response shape: { rows, page, pageSize, total, totalPages }
      setRows(data?.rows || []);
      setTotal(data?.total || 0);
      setTotalPages(data?.totalPages || 1);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Failed to load calendar days");
      setRows([]); setTotal(0); setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, futureOnly]);

  useEffect(() => { load(); }, [load]);

  // When user toggles futureOnly or pageSize, snap back to page 1.
  const changeFutureOnly = (v) => { setFutureOnly(v); setPage(1); };
  const changePageSize   = (v) => { setPageSize(v);   setPage(1); };

  const startRow = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endRow   = Math.min(page * pageSize, total);

  // Column template
  // Date | Type | Holiday | Holiday Prox | 1st | 15th | Last BDay | Friday
  const GRID = "1.6fr 0.9fr 1.6fr 0.7fr 0.5fr 0.5fr 0.7fr 0.5fr";

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1, mt: 3 }}>
        <SectionTitle>Calendar days</SectionTitle>
        <Typography sx={{ fontSize: 11, color: "text.disabled", ml: 0.5 }}>
          {total > 0
            ? `Showing ${startRow.toLocaleString()}–${endRow.toLocaleString()} of ${total.toLocaleString()} days`
            : "No days match the current filter"}
        </Typography>
      </Box>

      {/* ── Controls bar ── */}
      <Box sx={{
        display: "flex", alignItems: "center", gap: 2, mb: 1.5,
        px: 1, py: 1,
        background: "#f7f7f7",
        borderRadius: 1,
        border: "0.5px solid",
        borderColor: "divider",
      }}>
        <FormControlLabel
          control={
            <Switch
              size="small" checked={futureOnly}
              onChange={(e) => changeFutureOnly(e.target.checked)}
              sx={{ ".MuiSwitch-thumb": { color: futureOnly ? BLUE : undefined } }}
            />
          }
          label={<Typography sx={{ fontSize: 12 }}>Future only (from today)</Typography>}
        />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 1 }}>
          <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
            Rows per page:
          </Typography>
          <Select
            value={pageSize}
            onChange={(e) => changePageSize(Number(e.target.value))}
            size="small" variant="outlined"
            sx={{
              fontSize: 12, height: 28, minWidth: 64,
              ".MuiOutlinedInput-notchedOutline": { borderColor: "divider", borderWidth: "0.5px" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BLUE },
              ".MuiSelect-select": { py: "3px", px: "8px", fontSize: 12 },
              ".MuiSvgIcon-root": { fontSize: 16 },
            }}
          >
            {PAGE_SIZE_OPTIONS.map(n => (
              <MenuItem key={n} value={n} sx={{ fontSize: 12 }}>{n}</MenuItem>
            ))}
          </Select>
        </Box>

        {/* Pagination on the right */}
        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            size="small"
            disabled={loading || page <= 1}
            onClick={() => setPage(1)}
            sx={{ fontSize: 14, color: "text.secondary" }}
            title="First page"
          >⏮</IconButton>
          <IconButton
            size="small"
            disabled={loading || page <= 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
            sx={{ fontSize: 14, color: "text.secondary" }}
            title="Previous page"
          >◀</IconButton>
          <Typography sx={{ fontSize: 12, color: "text.secondary", minWidth: 70, textAlign: "center" }}>
            Page {page} of {totalPages}
          </Typography>
          <IconButton
            size="small"
            disabled={loading || page >= totalPages}
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            sx={{ fontSize: 14, color: "text.secondary" }}
            title="Next page"
          >▶</IconButton>
          <IconButton
            size="small"
            disabled={loading || page >= totalPages}
            onClick={() => setPage(totalPages)}
            sx={{ fontSize: 14, color: "text.secondary" }}
            title="Last page"
          >⏭</IconButton>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      {loading && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", py: 3, justifyContent: "center" }}>
          <CircularProgress size={18} />
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Loading calendar days…</Typography>
        </Box>
      )}

      {!loading && rows.length > 0 && (
        <Box sx={{
          border: "0.5px solid", borderColor: "divider",
          borderRadius: 2, overflow: "hidden",
        }}>
          {/* Header */}
          <Box sx={{
            display: "grid", gridTemplateColumns: GRID, gap: 1,
            px: 1.5, py: 1,
            background: "#f7f7f7",
            borderBottom: "0.5px solid", borderColor: "divider",
            fontSize: 11, fontWeight: 500,
            textTransform: "uppercase", letterSpacing: "0.05em",
            color: "text.secondary",
          }}>
            <Box>Date</Box>
            <Box>Type</Box>
            <Box>Holiday</Box>
            <Box sx={{ textAlign: "center" }} title="Days until nearest holiday (— if none nearby)">
              Hol Prox
            </Box>
            <Box sx={{ textAlign: "center" }} title="Days until/since nearest 1st of month">
              → 1st
            </Box>
            <Box sx={{ textAlign: "center" }} title="Days until/since nearest 15th of month">
              → 15th
            </Box>
            <Box sx={{ textAlign: "center" }} title="Days until nearest last business day of month">
              → Last BDay
            </Box>
            <Box sx={{ textAlign: "center" }} title="Days until nearest Friday (0 if Friday)">
              → Friday
            </Box>
          </Box>

          {/* Data rows */}
          {rows.map((row, idx) => {
            const dateISO = (row.date || "").slice(0, 10);
            const isToday = dateISO === today;
            const tintBg  = row.is_holiday   ? `${holidayTypeColor(row.holiday_type)}08`
                          : row.is_weekend   ? "rgba(0,0,0,0.025)"
                          : idx % 2 === 0    ? "transparent"
                                              : "rgba(0,0,0,0.012)";
            return (
              <Box key={dateISO} sx={{
                display: "grid", gridTemplateColumns: GRID, gap: 1,
                px: 1.5, py: "7px",
                borderBottom: "0.5px solid", borderColor: "divider",
                background: tintBg,
                borderLeft: isToday ? `3px solid ${BLUE}` : "3px solid transparent",
                "&:last-of-type": { borderBottom: "none" },
                alignItems: "center",
              }}>
                {/* Date */}
                <Box sx={{ fontSize: 12, color: isToday ? BLUE : "text.primary", fontWeight: isToday ? 600 : 400 }}>
                  {fmtDate(row.date)}
                  {isToday && (
                    <Box component="span" sx={{
                      ml: 0.75, fontSize: 9, fontWeight: 700,
                      color: BLUE, textTransform: "uppercase",
                      letterSpacing: "0.06em",
                    }}>
                      Today
                    </Box>
                  )}
                </Box>

                {/* Type badge */}
                <Box><DayTypeBadge row={row} /></Box>

                {/* Holiday name */}
                <Box sx={{ fontSize: 11.5, color: row.is_holiday ? holidayTypeColor(row.holiday_type) : "text.disabled" }}>
                  {row.holiday_name || "—"}
                </Box>

                {/* Proximities */}
                <Box sx={{ textAlign: "center" }}>
                  <ProxChip value={row.holiday_proximity} sentinel={99} color={AMBER} />
                </Box>
                <Box sx={{ textAlign: "center" }}>
                  <ProxChip value={row.proximity_to_1st} color={BLUE} />
                </Box>
                <Box sx={{ textAlign: "center" }}>
                  <ProxChip value={row.proximity_to_15th} color={BLUE} />
                </Box>
                <Box sx={{ textAlign: "center" }}>
                  <ProxChip value={row.proximity_to_last_bday} color={PURPLE} />
                </Box>
                <Box sx={{ textAlign: "center" }}>
                  <ProxChip value={row.proximity_to_friday} color={GREEN} />
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {!loading && rows.length === 0 && !error && (
        <Box sx={{ p: 3, textAlign: "center", color: "text.disabled", fontSize: 12 }}>
          No calendar days match the current filter.
          {futureOnly && " Try turning off 'Future only' to include past dates."}
        </Box>
      )}
    </Box>
  );
}


export default function CalendarView() {
  const [holidays,    setHolidays]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState("");
  const [toast,       setToast]       = useState({ open: false, message: "", severity: "success" });

  // Modal state
  const [holidayModal,    setHolidayModal]    = useState({ open: false, mode: "create", row: null });
  const [factorModalOpen, setFactorModalOpen] = useState(false);

  // ── Load holidays ──────────────────────────────────────────────
  const loadHolidays = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const data = await getForecastHolidays();
      setHolidays(data || []);
    } catch (e) {
      setError("Failed to load holidays");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadHolidays(); }, [loadHolidays]);

  // Group by type
  const grouped = useMemo(() => {
    const out = { federal: [], state: [], behavioral: [], other: [] };
    holidays.forEach(h => {
      const t = String(h.holiday_type || "other").toLowerCase();
      if (out[t]) out[t].push(h);
      else        out.other.push(h);
    });
    return out;
  }, [holidays]);

  // ── Action handlers ────────────────────────────────────────────
  const handleAddHoliday  = ()    => setHolidayModal({ open: true, mode: "create", row: null });
  const handleEditHoliday = (row) => setHolidayModal({ open: true, mode: "edit",   row });

  const handleDeleteHoliday = async (row) => {
    const ok = window.confirm(`Delete the holiday "${row.name}"?\n\nThis soft-deletes it.`);
    if (!ok) return;
    try {
      await deleteHoliday(row.id);
      setToast({ open: true, message: `Deleted "${row.name}"`, severity: "success" });
      loadHolidays();
    } catch (e) {
      setToast({ open: true, message: `Delete failed: ${e?.response?.data?.error || e?.message}`, severity: "error" });
    }
  };

  const handleSavedHoliday = (mode) => {
    setToast({
      open: true,
      message: mode === "create" ? "Holiday created" : "Holiday updated",
      severity: "success",
    });
    loadHolidays();
  };

  // ── Cell renderers ─────────────────────────────────────────────
  const renderCell = (col, row) => {
    if (col.type === "type")   return <TypePill type={row.holiday_type} />;
    if (col.type === "rule")   return (
      <Box component="span" sx={{ color: "text.secondary", fontSize: 12 }}>
        {summarizeRule(row.rule_type, row.rule_config)}
      </Box>
    );
    if (col.type === "status") return (
      <Box component="span" sx={{
        display: "inline-block", px: "7px", py: "1px",
        borderRadius: "20px", fontSize: 11, fontWeight: 500,
        bgcolor: row.is_active ? `${GREEN}18` : "#88888818",
        color:   row.is_active ? GREEN : "#888",
        border:  `0.5px solid ${row.is_active ? `${GREEN}44` : "#88888844"}`,
      }}>
        {row.is_active ? "Active" : "Inactive"}
      </Box>
    );
    if (col.type === "actions") return (
      <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
        <Box
          onClick={() => handleEditHoliday(row)}
          sx={{
            px: "10px", py: "3px", fontSize: 11, borderRadius: "20px",
            cursor: "pointer", userSelect: "none",
            border: "0.5px solid", borderColor: BLUE, color: BLUE, fontWeight: 500,
            transition: "all .15s",
            "&:hover": { background: `${BLUE}18` },
          }}
        >Edit</Box>
        <Box
          onClick={() => handleDeleteHoliday(row)}
          sx={{
            px: "10px", py: "3px", fontSize: 11, borderRadius: "20px",
            cursor: "pointer", userSelect: "none",
            border: "0.5px solid", borderColor: RED, color: RED, fontWeight: 500,
            transition: "all .15s",
            "&:hover": { background: `${RED}18` },
          }}
        >Delete</Box>
      </Box>
    );
    if (col.key === "name") return (
      <Box sx={{ fontWeight: 500, color: "text.primary" }}>{row[col.key]}</Box>
    );
    return <Box component="span">{row[col.key]}</Box>;
  };

  // ── Section renderer ───────────────────────────────────────────
  const renderSection = (title, rows, color) => {
    if (!rows || rows.length === 0) return null;
    return (
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1, mt: 2 }}>
          <Typography sx={{
            fontSize: 11, fontWeight: 500, color,
            textTransform: "uppercase", letterSpacing: "0.06em",
          }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: 11, color: "text.disabled" }}>
            ({rows.length})
          </Typography>
        </Box>

        <Box sx={{
          border: "0.5px solid", borderColor: "divider",
          borderRadius: 2, overflow: "hidden",
        }}>
          <Box sx={{
            display: "grid", gridTemplateColumns: GRID, px: 2, py: 1,
            background: "#f7f7f7", borderBottom: "0.5px solid", borderColor: "divider",
            fontSize: 11, fontWeight: 500,
            textTransform: "uppercase", letterSpacing: "0.05em",
          }}>
            {COLS.map(col => (
              <Box key={col.key} sx={{ textAlign: col.align, color: "text.secondary" }}>
                {col.label}
              </Box>
            ))}
          </Box>
          {rows.map((row, i) => (
            <Box key={row.id} sx={{
              display: "grid", gridTemplateColumns: GRID, px: 2, py: "10px",
              fontSize: 12, borderBottom: "0.5px solid", borderColor: "divider",
              background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
              "&:hover": { background: "rgba(55,138,221,0.06)" },
              "&:last-of-type": { borderBottom: "none" },
            }}>
              {COLS.map(col => (
                <Box key={col.key} sx={{
                  display: "flex", alignItems: "center", minWidth: 0,
                  justifyContent: col.align === "right" ? "flex-end" : "flex-start",
                }}>
                  {renderCell(col, row)}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 2 }}>

      {/* ── Header with action buttons on right ── */}
      <Box sx={{
        display: "flex", alignItems: "center", gap: 2,
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        <Typography sx={{ fontSize: 14, fontWeight: 500, color: "text.primary" }}>
          Calendar & Holidays
        </Typography>
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          2026-01-01 → 2029-12-31
        </Typography>

        <Box sx={{ ml: "auto", display: "flex", gap: 1.5, alignItems: "center" }}>
          {/* Edit factors — opens the day-of-month / day-of-week / holiday
              factor editor that shapes the Compare projection. Applies live;
              no calendar regenerate required. */}
          <Box
            onClick={() => setFactorModalOpen(true)}
            title="Edit the day-of-month / day-of-week / holiday factors that shape the Compare projection. Applies live."
            sx={{
              fontSize: 11, color: BLUE,
              cursor: "pointer", userSelect: "none",
              textDecoration: "underline dotted", opacity: 0.85,
              "&:hover": { opacity: 1 },
            }}
          >
            ✦ Edit factors
          </Box>
          <ActionButton onClick={handleAddHoliday} icon="+" color={BLUE}>
            New Holiday
          </ActionButton>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 2 }}>
          <CircularProgress size={20} /> Loading holidays…
        </Box>
      )}

      {!loading && holidays.length === 0 && (
        <EmptyState
          title="No holidays defined"
          hint='Click "+ New Holiday" to add one, or run 005_seed_holidays.sql to populate the standard set.'
        />
      )}

      {!loading && holidays.length > 0 && (
        <>
          {renderSection("Federal holidays",    grouped.federal,    BLUE)}
          {renderSection("State holidays",      grouped.state,      AMBER)}
          {renderSection("Behavioral holidays", grouped.behavioral, PURPLE)}
          {renderSection("Other",               grouped.other,      "#888")}

          <SectionTitle>Calendar status</SectionTitle>
          <Box sx={{
            border: "0.5px solid", borderColor: "divider", borderRadius: 2,
            p: 2, fontSize: 12, color: "text.secondary",
          }}>
            The calendar table covers <Box component="span" sx={{ color: "text.primary", fontWeight: 500 }}>
              Jan 1, 2026 - Dec 31, 2029
            </Box> (1,461 days). It includes day-of-week, holiday flags, holiday proximity,
            and payday-proximity columns (1st / 15th / last business day / nearest Friday).
            Holiday edits take effect on the next forecast run. Use{" "}
            <Box component="span" sx={{ color: BLUE, fontWeight: 500 }}>✦ Edit factors</Box>{" "}
            (top right) to tune the day-of-month / day-of-week / holiday
            multipliers that shape the Compare projection — those apply
            immediately, no rebuild needed.
          </Box>
        </>
      )}

      {/* ── Calendar Days (paginated day-by-day view) ── */}
      <CalendarDaysTable />

      {/* ── Modals ── */}
      <HolidayFormModal
        open={holidayModal.open}
        mode={holidayModal.mode}
        initialData={holidayModal.row}
        onClose={() => setHolidayModal({ ...holidayModal, open: false })}
        onSaved={() => handleSavedHoliday(holidayModal.mode)}
      />

      <CalendarFactorModal
        open={factorModalOpen}
        onClose={() => setFactorModalOpen(false)}
        onSaved={() => { /* factors apply live to Compare; nothing to reload here */ }}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast({ ...toast, open: false })}
          severity={toast.severity}
          sx={{ fontSize: 13 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}